import React, { useEffect, useRef, useState } from 'react'
import { FaPlus, FaTrash } from 'react-icons/fa'
import {
  Btn,
  CustomCalendar,
  CustomCheckbox,
  CustomDropdown,
  CustomModal,
  InputNumeroDecimal,
  InputNumeroInteiro,
  InputText,
  Loading,
  SectionTitle,
} from '../../../../components'
import { getUfs } from '../../../../services/api/https/system/system.http'
import {
  getDadosDeclaracaoCiot,
  getPisoMinimoCiot,
  postDeclararCiot,
} from '../../../../services/api/https/deliveries/ciot.http'
import SelectMunicipioCiot from './SelectMunicipioCiot'
import {
  BadgeStatusCiot,
  CIOT_FRACIONADA,
  CIOT_LOTACAO,
  PAGAMENTO_PIX,
  PAGAMENTOS_BANCARIOS,
  TIPOS_CARGA_CIOT,
  TIPOS_OPERACAO_CIOT,
  TIPOS_PAGAMENTO_CIOT,
  dataIsoParaDate,
  dateParaDataIso,
  estiloAvisoCiot,
  estiloDicaCiot,
  estiloErroCiot,
  formatarDocumento,
  formatarMoeda,
  formatarPlaca,
  normalizarDocumento,
} from './ciot.util'

const MAXIMO_VEICULOS = 5

const somenteDigitos = (valor, maximo) =>
  String(valor || '')
    .replace(/\D/g, '')
    .slice(0, maximo)

const novoTrecho = origem => ({
  codigo_municipio_origem: origem ? origem.codigo_municipio_origem : null,
  nome_municipio_origem: origem ? origem.nome_municipio_origem : null,
  codigo_municipio_destino: null,
  nome_municipio_destino: null,
  distancia_km: null,
  distancia_estimada: false,
})

// O Calendar guarda o valor bruto (Date ou texto parcial); a conversão para AAAA-MM-DD fica para o envio
const datasDoForm = dados => ({
  inicio: dataIsoParaDate(dados.data_inicio_viagem),
  fim: dataIsoParaDate(dados.data_fim_viagem),
  parcelas: ((dados.pagamento && dados.pagamento.parcelas) || []).map(parcela =>
    dataIsoParaDate(parcela.data_vencimento),
  ),
})

const dataDoCalendario = valor => (valor instanceof Date ? dateParaDataIso(valor) : '')

const opcao = (opcoes, valor) => opcoes.find(item => item.value === Number(valor)) || null

export default function ModalDeclararCiot({
  visible,
  ciot,
  emissor,
  contexto = {},
  onClose,
  onDeclarado,
  onError,
}) {
  const [carregando, setCarregando] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [form, setForm] = useState(null)
  const [datas, setDatas] = useState({ inicio: null, fim: null, parcelas: [] })
  const [avisos, setAvisos] = useState([])
  const [transportador, setTransportador] = useState(null)
  const [erros, setErros] = useState([])
  const [ciotAtual, setCiotAtual] = useState(ciot)
  const [ufs, setUfs] = useState([])
  const [piso, setPiso] = useState(null)
  const caixaErros = useRef(null)
  const ultimoPiso = useRef(0)

  const parametrosPiso =
    form && form.tipo_operacao === CIOT_LOTACAO
      ? {
          tipoCarga: Number(form.codigo_tipo_carga) || null,
          eixos: (form.veiculos || []).reduce(
            (soma, veiculo) => soma + (Number(veiculo.eixos) || 0),
            0,
          ),
          distanciaKm: (form.origens_destinos || []).reduce(
            (soma, trecho) => soma + (Number(trecho.distancia_km) || 0),
            0,
          ),
          composicaoVeicular: Boolean(form.composicao_veicular),
          altoDesempenho: Boolean(form.alto_desempenho),
          retornoVazio: Boolean(form.retorno_vazio),
        }
      : null

  useEffect(() => {
    if (erros.length && caixaErros.current)
      caixaErros.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [erros])

  // A ANTT recusa carga lotação com frete abaixo do piso mínimo (código 291)
  useEffect(() => {
    const busca = ++ultimoPiso.current
    if (!parametrosPiso) {
      setPiso(null)
      return
    }
    if (!parametrosPiso.tipoCarga || !parametrosPiso.eixos || !parametrosPiso.distanciaKm) {
      setPiso({ incompleto: true })
      return
    }
    setPiso(prev => ({ valor: prev && prev.valor, carregando: true }))
    const espera = setTimeout(() => {
      getPisoMinimoCiot(parametrosPiso)
        .then(valor => busca === ultimoPiso.current && setPiso({ valor }))
        .catch(() => busca === ultimoPiso.current && setPiso({ erro: true }))
    }, 600)
    return () => clearTimeout(espera)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(parametrosPiso)])

  useEffect(() => {
    getUfs().then(setUfs).catch(onError)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!visible || !ciot || !emissor) return
    let ativo = true
    setCarregando(true)
    setForm(null)
    setErros(ciot.status === 'recusado' && ciot.mensagem ? ciot.mensagem.split('\n') : [])
    setCiotAtual(ciot)
    getDadosDeclaracaoCiot({
      emissorId: emissor.id,
      ciotId: ciot.id,
      fleetId: contexto.fleetId || ciot.fleet_id,
      ctesIds: contexto.ctesIds,
      deliveriesIds: contexto.deliveriesIds,
      vehicleId: contexto.vehicleId,
      tipoCarga: contexto.tipoCarga,
    })
      .then(resposta => {
        if (!ativo) return
        const { dados } = resposta
        setForm({
          ...dados,
          codigo_natureza_carga: dados.codigo_natureza_carga
            ? String(dados.codigo_natureza_carga).padStart(4, '0')
            : '',
        })
        setDatas(datasDoForm(dados))
        setAvisos(resposta.avisos || [])
        setTransportador(resposta.transportador || null)
      })
      .catch(error => {
        if (!ativo) return
        onError(error)
        onClose()
      })
      .finally(() => ativo && setCarregando(false))
    return () => {
      ativo = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, ciot && ciot.id])

  const alterar = (campo, valor) => setForm(prev => ({ ...prev, [campo]: valor }))

  const alterarItem = (lista, indice, campos) =>
    setForm(prev => ({
      ...prev,
      [lista]: prev[lista].map((item, i) => (i === indice ? { ...item, ...campos } : item)),
    }))

  const removerItem = (lista, indice) =>
    setForm(prev => ({ ...prev, [lista]: prev[lista].filter((_, i) => i !== indice) }))

  const alterarPagamento = campos =>
    setForm(prev => ({ ...prev, pagamento: { ...prev.pagamento, ...campos } }))

  // Parcela única ainda não mexida acompanha o valor do frete
  const alterarValorFrete = valor =>
    setForm(prev => {
      const pagamento = prev.pagamento || {}
      const parcelas = pagamento.parcelas || []
      const acompanha =
        pagamento.a_prazo &&
        parcelas.length === 1 &&
        (!Number(parcelas[0].valor) || Number(parcelas[0].valor) === Number(prev.valor_frete))
      return {
        ...prev,
        valor_frete: valor,
        pagamento: acompanha ? { ...pagamento, parcelas: [{ ...parcelas[0], valor }] } : pagamento,
      }
    })

  const alterarParcela = (indice, campos) =>
    alterarPagamento({
      parcelas: form.pagamento.parcelas.map((parcela, i) =>
        i === indice ? { ...parcela, ...campos } : parcela,
      ),
    })

  const adicionarParcela = () => {
    alterarPagamento({
      parcelas: [
        ...(form.pagamento.parcelas || []),
        { numero: (form.pagamento.parcelas || []).length + 1, data_vencimento: null, valor: null },
      ],
    })
    setDatas(prev => ({ ...prev, parcelas: [...prev.parcelas, null] }))
  }

  const removerParcela = indice => {
    alterarPagamento({ parcelas: form.pagamento.parcelas.filter((_, i) => i !== indice) })
    setDatas(prev => ({ ...prev, parcelas: prev.parcelas.filter((_, i) => i !== indice) }))
  }

  const montarDados = () => ({
    ...form,
    cpf_cnpj_contratante: normalizarDocumento(form.cpf_cnpj_contratante),
    cpf_cnpj_destinatario: normalizarDocumento(form.cpf_cnpj_destinatario),
    codigo_natureza_carga: form.codigo_natureza_carga ? Number(form.codigo_natureza_carga) : null,
    contratantes_carga_fracionada:
      form.tipo_operacao === CIOT_FRACIONADA
        ? (form.contratantes_carga_fracionada || []).map(normalizarDocumento).filter(Boolean)
        : [],
    data_inicio_viagem: dataDoCalendario(datas.inicio),
    data_fim_viagem: dataDoCalendario(datas.fim),
    veiculos: form.veiculos.map(veiculo => ({
      placa: formatarPlaca(veiculo.placa),
      eixos: veiculo.eixos === '' || veiculo.eixos === null ? null : Number(veiculo.eixos),
      rntrc: veiculo.rntrc ? somenteDigitos(veiculo.rntrc, 9) : null,
    })),
    pagamento: {
      ...form.pagamento,
      cpf_cnpj_creditado: normalizarDocumento(form.pagamento.cpf_cnpj_creditado),
      parcelas: form.pagamento.a_prazo
        ? (form.pagamento.parcelas || []).map((parcela, indice) => ({
            numero: indice + 1,
            data_vencimento: dataDoCalendario(datas.parcelas[indice]),
            valor: Number(parcela.valor) || 0,
          }))
        : [],
    },
  })

  const onBtnDeclararClicked = async () => {
    setEnviando(true)
    setErros([])
    try {
      const declarado = await postDeclararCiot(ciotAtual.id, montarDados())
      onDeclarado(declarado)
    } catch (error) {
      if (error && error.ciot) setCiotAtual(error.ciot)
      if (error && error.erros) setErros(error.erros)
      else onError(error)
    } finally {
      setEnviando(false)
    }
  }

  const renderDocumento = (label, valor, onChange, dica) => (
    <>
      <InputText
        label={label}
        marginTop={10}
        value={formatarDocumento(valor)}
        placeholder="CPF ou CNPJ"
        onChange={value => onChange(normalizarDocumento(value))}
      />
      {dica ? <div style={estiloDicaCiot}>{dica}</div> : null}
    </>
  )

  const renderPiso = () => {
    if (piso.valor) {
      const { valor_minimo: minimo, tabela, distancia_km: km, eixos } = piso.valor
      const detalhe = `${formatarMoeda(minimo)} (${tabela.replace('Tabela', 'tabela')}, ${km} km, ${eixos} eixos)`
      const frete = Number(form.valor_frete)
      if (frete > 0 && frete < minimo)
        return (
          <div style={estiloAvisoCiot}>
            O valor do frete está abaixo do piso mínimo da ANTT para esta operação: {detalhe}. A
            ANTT recusa a declaração abaixo do piso.
          </div>
        )
      return <div style={estiloDicaCiot}>Piso mínimo da ANTT para esta operação: {detalhe}.</div>
    }
    if (piso.carregando)
      return <div style={estiloDicaCiot}>Calculando o piso mínimo da ANTT...</div>
    if (piso.erro)
      return (
        <div style={estiloDicaCiot}>
          Não foi possível consultar o piso mínimo da ANTT agora. A declaração com frete abaixo do
          piso é recusada.
        </div>
      )
    return (
      <div style={estiloDicaCiot}>
        Informe o tipo de carga, os eixos e a distância para ver o piso mínimo da ANTT.
      </div>
    )
  }

  const renderConteudo = () => {
    const lotacao = form.tipo_operacao === CIOT_LOTACAO
    const fracionada = form.tipo_operacao === CIOT_FRACIONADA
    const pagamento = form.pagamento || {}
    const tipoPagamento = Number(pagamento.tipo)
    const trechosEstimados = (form.origens_destinos || []).some(trecho => trecho.distancia_estimada)

    return (
      <div className="p-grid">
        {erros.length > 0 && (
          <div className="p-col-12">
            <div ref={caixaErros} style={estiloErroCiot}>
              <strong>A declaração não foi aceita:</strong>
              <ul style={{ margin: '5px 0 0 18px', padding: 0 }}>
                {erros.map((erro, indice) => (
                  <li key={indice}>{erro}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
        {avisos.map((aviso, indice) => (
          <div className="p-col-12" key={indice} style={{ paddingTop: 2, paddingBottom: 2 }}>
            <div style={estiloAvisoCiot}>{aviso}</div>
          </div>
        ))}

        <div className="p-col-12">
          <SectionTitle title="Operação" />
        </div>
        <div className="p-md-4 p-col-12">
          <CustomDropdown
            optionLabel="label"
            options={TIPOS_OPERACAO_CIOT}
            value={opcao(TIPOS_OPERACAO_CIOT, form.tipo_operacao)}
            label="Tipo de operação:*"
            onChange={value => alterar('tipo_operacao', value ? value.value : null)}
          />
          <div style={estiloDicaCiot}>
            Lotação: um só contratante. Fracionada: carga de vários contratantes no mesmo veículo.
          </div>
        </div>
        <div className="p-md-3 p-col-6">
          <CustomCalendar
            label="Início da viagem:*"
            value={datas.inicio}
            appendTo={document.body}
            onChange={value => setDatas(prev => ({ ...prev, inicio: value }))}
          />
        </div>
        <div className="p-md-3 p-col-6">
          <CustomCalendar
            label="Fim da viagem:*"
            value={datas.fim}
            appendTo={document.body}
            onChange={value => setDatas(prev => ({ ...prev, fim: value }))}
          />
        </div>
        <div className="p-md-2 p-col-12">
          <InputNumeroDecimal
            label="Valor do frete (R$):*"
            marginTop={10}
            value={form.valor_frete}
            onChange={alterarValorFrete}
          />
        </div>
        {lotacao && piso && (
          <div className="p-col-12" style={{ paddingTop: 0, paddingBottom: 0 }}>
            {renderPiso()}
          </div>
        )}

        <div className="p-col-12" style={{ marginTop: 10 }}>
          <SectionTitle title="Contratado, contratante e destinatário" />
        </div>
        <div className="p-md-5 p-col-12">
          <InputText
            label="Contratado (transportadora do certificado):"
            marginTop={10}
            disabled={true}
            value={`${formatarDocumento(form.cpf_cnpj_contratado)} - ${
              transportador || emissor.razao_social
            }`}
            onChange={() => {}}
          />
        </div>
        <div className="p-md-2 p-col-12">
          <InputText
            label="RNTRC do contratado:*"
            marginTop={10}
            value={form.rntrc_contratado || ''}
            onChange={value => alterar('rntrc_contratado', somenteDigitos(value, 9))}
          />
        </div>
        <div className="p-md-3 p-col-12">
          {renderDocumento(
            'Contratante:*',
            form.cpf_cnpj_contratante,
            value => alterar('cpf_cnpj_contratante', value),
            'Quem contrata o frete (tomador do CT-e)',
          )}
        </div>
        <div className="p-md-2 p-col-12">
          <InputText
            label="RNTRC do contratante:"
            marginTop={10}
            value={form.rntrc_contratante || ''}
            placeholder="se tiver"
            onChange={value => alterar('rntrc_contratante', somenteDigitos(value, 9) || null)}
          />
        </div>
        <div className="p-md-4 p-col-12">
          {renderDocumento('Destinatário:*', form.cpf_cnpj_destinatario, value =>
            alterar('cpf_cnpj_destinatario', value),
          )}
        </div>
        {fracionada && (
          <div className="p-col-12">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: 10,
              }}
            >
              <span style={{ fontSize: '0.8rem', color: '#14B0BF' }}>
                Demais contratantes da carga fracionada:*
              </span>
              <Btn
                onClick={() =>
                  alterar('contratantes_carga_fracionada', [
                    ...(form.contratantes_carga_fracionada || []),
                    '',
                  ])
                }
              >
                <FaPlus style={{ marginRight: 5 }} size={10} />
                Adicionar contratante
              </Btn>
            </div>
            <div className="p-grid">
              {(form.contratantes_carga_fracionada || []).map((documento, indice) => (
                <div className="p-md-4 p-col-12" key={indice}>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      {renderDocumento(`Contratante ${indice + 2}:`, documento, value =>
                        alterar(
                          'contratantes_carga_fracionada',
                          form.contratantes_carga_fracionada.map((item, i) =>
                            i === indice ? value : item,
                          ),
                        ),
                      )}
                    </div>
                    <FaTrash
                      style={{ cursor: 'pointer', marginBottom: 8 }}
                      color="red"
                      onClick={() => removerItem('contratantes_carga_fracionada', indice)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="p-col-12" style={{ marginTop: 10 }}>
          <SectionTitle title="Veículos" />
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 5,
            }}
          >
            <span style={estiloDicaCiot}>
              Primeiro o veículo de tração (2 a 4 eixos); depois os implementos (1 a 4 eixos), até{' '}
              {MAXIMO_VEICULOS} placas. As placas precisam estar na frota do RNTRC da
              transportadora.
            </span>
            <Btn
              disabled={form.veiculos.length >= MAXIMO_VEICULOS}
              onClick={() =>
                form.veiculos.length < MAXIMO_VEICULOS &&
                setForm(prev => ({
                  ...prev,
                  veiculos: [...prev.veiculos, { placa: '', eixos: null, rntrc: null }],
                  composicao_veicular: true,
                }))
              }
            >
              <FaPlus style={{ marginRight: 5 }} size={10} />
              Adicionar implemento
            </Btn>
          </div>
        </div>
        {form.veiculos.map((veiculo, indice) => (
          <React.Fragment key={indice}>
            <div className="p-md-3 p-col-6">
              <InputText
                label={indice === 0 ? 'Placa do veículo:*' : `Placa do implemento ${indice}:*`}
                marginTop={10}
                value={veiculo.placa || ''}
                onChange={value => alterarItem('veiculos', indice, { placa: formatarPlaca(value) })}
              />
            </div>
            <div className="p-md-2 p-col-6">
              <InputNumeroInteiro
                label="Eixos:*"
                marginTop={10}
                allowNegative={false}
                format="#"
                value={veiculo.eixos === null || veiculo.eixos === undefined ? '' : veiculo.eixos}
                onChange={value => alterarItem('veiculos', indice, { eixos: value })}
              />
            </div>
            <div className="p-md-3 p-col-10">
              <InputText
                label="RNTRC do proprietário:"
                marginTop={10}
                value={veiculo.rntrc || ''}
                placeholder="só se o veículo for de terceiro"
                onChange={value =>
                  alterarItem('veiculos', indice, { rntrc: somenteDigitos(value, 9) || null })
                }
              />
            </div>
            <div
              className="p-md-1 p-col-2"
              style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 15 }}
            >
              {indice > 0 && (
                <FaTrash
                  style={{ cursor: 'pointer' }}
                  color="red"
                  onClick={() => removerItem('veiculos', indice)}
                />
              )}
            </div>
            <div className="p-md-3 p-col-12" />
          </React.Fragment>
        ))}
        {!form.veiculos.length && (
          <div className="p-col-12">
            <Btn onClick={() => alterar('veiculos', [{ placa: '', eixos: null, rntrc: null }])}>
              Informar veículo
            </Btn>
          </div>
        )}

        <div className="p-col-12" style={{ marginTop: 10 }}>
          <SectionTitle title="Origem e destino" />
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 5,
            }}
          >
            <span style={estiloDicaCiot}>
              Um trecho por cidade de destino, com a distância rodada em km.
            </span>
            <Btn
              onClick={() =>
                alterar('origens_destinos', [
                  ...form.origens_destinos,
                  novoTrecho(form.origens_destinos[0]),
                ])
              }
            >
              <FaPlus style={{ marginRight: 5 }} size={10} />
              Adicionar trecho
            </Btn>
          </div>
        </div>
        {trechosEstimados && (
          <div className="p-col-12" style={{ paddingTop: 2, paddingBottom: 2 }}>
            <div style={estiloAvisoCiot}>
              As distâncias marcadas como estimadas foram calculadas em linha reta + 25%. Confira
              pela rota real: a ANTT avisa quando a distância fica abaixo da calculada por ela.
            </div>
          </div>
        )}
        {form.origens_destinos.map((trecho, indice) => (
          <React.Fragment key={indice}>
            <div className="p-md-5 p-col-12">
              <SelectMunicipioCiot
                labelUf="Origem UF:*"
                labelCidade="Origem:*"
                codigo={trecho.codigo_municipio_origem}
                ufs={ufs}
                onError={onError}
                onChange={cidade =>
                  alterarItem('origens_destinos', indice, {
                    codigo_municipio_origem: cidade ? cidade.codigo : null,
                    nome_municipio_origem: cidade ? cidade.nome : null,
                  })
                }
              />
            </div>
            <div className="p-md-4 p-col-12">
              <SelectMunicipioCiot
                labelUf="Destino UF:*"
                labelCidade="Destino:*"
                codigo={trecho.codigo_municipio_destino}
                ufs={ufs}
                onError={onError}
                onChange={cidade =>
                  alterarItem('origens_destinos', indice, {
                    codigo_municipio_destino: cidade ? cidade.codigo : null,
                    nome_municipio_destino: cidade ? cidade.nome : null,
                  })
                }
              />
            </div>
            <div className="p-md-2 p-col-10">
              <InputNumeroInteiro
                label={
                  trecho.distancia_estimada ? 'Distância (km, estimada):*' : 'Distância (km):*'
                }
                marginTop={10}
                allowNegative={false}
                value={
                  trecho.distancia_km === null || trecho.distancia_km === undefined
                    ? ''
                    : trecho.distancia_km
                }
                onChange={value =>
                  alterarItem('origens_destinos', indice, {
                    distancia_km: value === '' ? null : Number(value),
                    distancia_estimada: false,
                  })
                }
              />
            </div>
            <div
              className="p-md-1 p-col-2"
              style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 15 }}
            >
              {form.origens_destinos.length > 1 && (
                <FaTrash
                  style={{ cursor: 'pointer' }}
                  color="red"
                  onClick={() => removerItem('origens_destinos', indice)}
                />
              )}
            </div>
          </React.Fragment>
        ))}

        <div className="p-col-12" style={{ marginTop: 10 }}>
          <SectionTitle title="Carga" />
        </div>
        <div className="p-md-3 p-col-12">
          <InputText
            label="Natureza da carga:*"
            marginTop={10}
            placeholder="4 dígitos"
            value={form.codigo_natureza_carga || ''}
            onChange={value => alterar('codigo_natureza_carga', somenteDigitos(value, 4))}
          />
          <div style={estiloDicaCiot}>Posição do NCM (4 primeiros dígitos), ex.: 0401</div>
        </div>
        <div className="p-md-3 p-col-12">
          <InputNumeroDecimal
            label="Peso da carga (kg):*"
            marginTop={10}
            value={form.peso_carga}
            onChange={value => alterar('peso_carga', value)}
          />
        </div>
        <div className="p-md-6 p-col-12">
          <CustomDropdown
            optionLabel="label"
            options={TIPOS_CARGA_CIOT}
            value={opcao(TIPOS_CARGA_CIOT, form.codigo_tipo_carga)}
            label="Tipo de carga:*"
            filter={true}
            onChange={value => alterar('codigo_tipo_carga', value ? value.value : null)}
          />
        </div>

        <div className="p-col-12" style={{ marginTop: 10 }}>
          <SectionTitle title="Pagamento do frete" />
        </div>
        <div className="p-md-4 p-col-12">
          <CustomDropdown
            optionLabel="label"
            options={TIPOS_PAGAMENTO_CIOT}
            value={opcao(TIPOS_PAGAMENTO_CIOT, pagamento.tipo)}
            label="Forma de pagamento:*"
            onChange={value => alterarPagamento({ tipo: value ? value.value : null })}
          />
        </div>
        <div className="p-md-4 p-col-12">
          {renderDocumento('Quem recebe o frete:*', pagamento.cpf_cnpj_creditado, value =>
            alterarPagamento({ cpf_cnpj_creditado: value }),
          )}
        </div>
        <div className="p-md-4 p-col-12">
          <InputText
            label="Código do pagamento:"
            marginTop={10}
            value={pagamento.codigo_pagamento || ''}
            placeholder="boleto, depósito ou referência"
            onChange={value => alterarPagamento({ codigo_pagamento: value.slice(0, 50) || null })}
          />
        </div>
        {PAGAMENTOS_BANCARIOS.includes(tipoPagamento) && (
          <>
            <div className="p-md-2 p-col-4">
              <InputText
                label="Banco:*"
                marginTop={10}
                value={pagamento.codigo_instituicao_financeira || ''}
                placeholder="001"
                onChange={value =>
                  alterarPagamento({ codigo_instituicao_financeira: somenteDigitos(value, 3) })
                }
              />
            </div>
            <div className="p-md-3 p-col-8">
              <InputText
                label="Agência:*"
                marginTop={10}
                value={pagamento.agencia || ''}
                onChange={value => alterarPagamento({ agencia: value.slice(0, 10) })}
              />
            </div>
            <div className="p-md-4 p-col-12">
              <InputText
                label="Conta:*"
                marginTop={10}
                value={pagamento.conta || ''}
                onChange={value => alterarPagamento({ conta: value.slice(0, 20) })}
              />
            </div>
          </>
        )}
        {tipoPagamento === PAGAMENTO_PIX && (
          <>
            <div className="p-md-6 p-col-12">
              <InputText
                label="Chave Pix:*"
                marginTop={10}
                value={pagamento.chave_pix || ''}
                onChange={value => alterarPagamento({ chave_pix: value.slice(0, 77) })}
              />
            </div>
            <div className="p-md-6 p-col-12">
              <InputText
                label="Identificador do Pix:*"
                marginTop={10}
                value={pagamento.identificador_pix || ''}
                placeholder="até 32 caracteres"
                onChange={value => alterarPagamento({ identificador_pix: value.slice(0, 32) })}
              />
            </div>
          </>
        )}
        <div className="p-col-12" style={{ marginTop: 10 }}>
          <CustomCheckbox
            checked={Boolean(pagamento.a_prazo)}
            onChange={value => {
              alterarPagamento({
                a_prazo: value,
                parcelas: value
                  ? [{ numero: 1, data_vencimento: null, valor: form.valor_frete || null }]
                  : [],
              })
              setDatas(prev => ({ ...prev, parcelas: value ? [null] : [] }))
            }}
          >
            Pagamento a prazo (em parcelas)
          </CustomCheckbox>
        </div>
        {pagamento.a_prazo &&
          (pagamento.parcelas || []).map((parcela, indice) => (
            <React.Fragment key={indice}>
              <div className="p-md-3 p-col-6">
                <CustomCalendar
                  label={`Vencimento da parcela ${indice + 1}:*`}
                  value={datas.parcelas[indice] || null}
                  appendTo={document.body}
                  onChange={value =>
                    setDatas(prev => ({
                      ...prev,
                      parcelas: prev.parcelas.map((data, i) => (i === indice ? value : data)),
                    }))
                  }
                />
              </div>
              <div className="p-md-3 p-col-5">
                <InputNumeroDecimal
                  label="Valor (R$):*"
                  marginTop={10}
                  value={parcela.valor}
                  onChange={value => alterarParcela(indice, { valor: value })}
                />
              </div>
              <div
                className="p-md-1 p-col-1"
                style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 15 }}
              >
                {pagamento.parcelas.length > 1 && (
                  <FaTrash
                    style={{ cursor: 'pointer' }}
                    color="red"
                    onClick={() => removerParcela(indice)}
                  />
                )}
              </div>
              <div className="p-md-5 p-col-12" style={{ display: 'flex', alignItems: 'flex-end' }}>
                {indice === pagamento.parcelas.length - 1 && (
                  <Btn onClick={adicionarParcela}>
                    <FaPlus style={{ marginRight: 5 }} size={10} />
                    Adicionar parcela
                  </Btn>
                )}
              </div>
            </React.Fragment>
          ))}

        {lotacao && (
          <>
            <div className="p-col-12" style={{ marginTop: 10 }}>
              <SectionTitle title="Indicadores da operação" />
            </div>
            <div className="p-col-12" style={{ display: 'flex', gap: 25, flexWrap: 'wrap' }}>
              <div>
                <CustomCheckbox
                  checked={Boolean(form.alto_desempenho)}
                  onChange={value => alterar('alto_desempenho', value)}
                >
                  Alto desempenho
                </CustomCheckbox>
              </div>
              <div>
                <CustomCheckbox
                  checked={Boolean(form.retorno_vazio)}
                  onChange={value => alterar('retorno_vazio', value)}
                >
                  Retorno vazio
                </CustomCheckbox>
              </div>
              <div>
                <CustomCheckbox
                  checked={Boolean(form.composicao_veicular)}
                  onChange={value => alterar('composicao_veicular', value)}
                >
                  Composição veicular (caminhão simples ou veículo com implemento)
                </CustomCheckbox>
              </div>
            </div>
            <div className="p-col-12" style={{ ...estiloDicaCiot, paddingTop: 0 }}>
              Os indicadores definem a tabela do piso mínimo. Desmarque a composição veicular só
              quando for contratado apenas o cavalo mecânico, sem implemento.
            </div>
          </>
        )}

        <div
          className="p-col-12"
          style={{ display: 'flex', justifyContent: 'space-between', marginTop: 15 }}
        >
          <Btn backgroundColor="#999999" onClick={onClose}>
            Voltar
          </Btn>
          <Btn isLoading={enviando} onClick={onBtnDeclararClicked}>
            Declarar CIOT
          </Btn>
        </div>
      </div>
    )
  }

  return (
    <CustomModal
      isVisible={visible}
      header={
        <span>
          Declarar CIOT {ciotAtual ? ciotAtual.numero : ''}{' '}
          {ciotAtual ? <BadgeStatusCiot status={ciotAtual.status} /> : null}
        </span>
      }
      style={{ width: '90vw', maxWidth: 1000 }}
      contentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
      onClose={onClose}
    >
      {carregando || !form ? (
        <div className="text-center" style={{ marginTop: 20, marginBottom: 20 }}>
          <Loading />
        </div>
      ) : (
        renderConteudo()
      )}
    </CustomModal>
  )
}
